<template>
  <div class="page-card">
    <div class="page-head">
      <h3>PBSQ Question Bank</h3>
      <el-button type="primary" size="small" @click="openCreate">New Question</el-button>
    </div>

    <el-form :inline="true" :model="query" size="small" class="search-form">
      <el-form-item label="Keyword">
        <el-input v-model="query.keyword" clearable placeholder="Code / title / hint / answer" @keyup.enter.native="search" />
      </el-form-item>
      <el-form-item label="Domain">
        <el-select v-model="query.domainKey" clearable placeholder="All">
          <el-option v-for="item in domainOptions" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
      </el-form-item>
      <el-form-item label="Status">
        <el-select v-model="query.status" clearable placeholder="All">
          <el-option label="enabled" value="enabled" />
          <el-option label="disabled" value="disabled" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="search">Search</el-button>
        <el-button @click="resetSearch">Reset</el-button>
      </el-form-item>
    </el-form>

    <el-table :data="list" v-loading="loading" border size="small">
      <el-table-column prop="id" label="ID" width="70" />
      <el-table-column label="Domain" width="110">
        <template slot-scope="scope">{{ domainLabel(scope.row.domainKey) }}</template>
      </el-table-column>
      <el-table-column prop="questionCode" label="Code" width="90" />
      <el-table-column prop="title" label="Question" min-width="220" show-overflow-tooltip />
      <el-table-column prop="hint" label="Hint" min-width="220" show-overflow-tooltip />
      <el-table-column prop="weightLabel" label="Weight" width="90" />
      <el-table-column label="Answers" min-width="260" show-overflow-tooltip>
        <template slot-scope="scope">{{ optionsSummary(scope.row.optionsJson) }}</template>
      </el-table-column>
      <el-table-column prop="status" label="Status" width="100">
        <template slot-scope="scope">
          <el-tag :type="scope.row.status === 'enabled' ? 'success' : 'info'" size="small">{{ scope.row.status }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="sortOrder" label="Sort" width="80" />
      <el-table-column label="Actions" width="150" fixed="right">
        <template slot-scope="scope">
          <el-button type="text" size="small" @click="openEdit(scope.row)">Edit</el-button>
          <el-button type="text" size="small" class="danger" @click="remove(scope.row)">Delete</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      class="pager"
      background
      layout="total, sizes, prev, pager, next"
      :total="total"
      :page-size="query.pageSize"
      :current-page="query.pageNum"
      :page-sizes="[10, 20, 50, 100]"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
    />

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="720px">
      <el-form :model="form" label-width="120px" size="small">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="Domain">
              <el-select v-model="form.domainKey" style="width:100%">
                <el-option v-for="item in domainOptions" :key="item.value" :label="item.label" :value="item.value" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Question Code">
              <el-input v-model="form.questionCode" placeholder="A1" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Weight">
              <el-input v-model="form.weightLabel" placeholder="40%" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Sort">
              <el-input-number v-model="form.sortOrder" :min="0" style="width:100%" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Status">
              <el-select v-model="form.status" style="width:100%">
                <el-option label="enabled" value="enabled" />
                <el-option label="disabled" value="disabled" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="Question Title">
          <el-input v-model="form.title" placeholder="A1 - Heart Rate Recovery" />
        </el-form-item>
        <el-form-item label="Hint">
          <el-input v-model="form.hint" placeholder="How quickly did your HR return to normal?" />
        </el-form-item>
        <el-form-item label="Answer Options">
          <el-input
            v-model="form.optionsText"
            type="textarea"
            :rows="6"
            placeholder="One answer option per line. The line order is the saved answer value."
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible=false">Cancel</el-button>
        <el-button type="primary" @click="submit">Save</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { pbsqQuestionApi } from '@/api/pbsq'

const domainOptions = [
  { value: 'a', label: 'Domain A' },
  { value: 'b', label: 'Domain B' },
  { value: 'c', label: 'Domain C' },
  { value: 'd', label: 'Domain D' },
  { value: 'e', label: 'Domain E' }
]

const emptyQuery = () => ({ pageNum: 1, pageSize: 10, keyword: '', domainKey: '', status: '' })

const emptyForm = () => ({
  id: null,
  flowType: 'full',
  domainKey: 'a',
  questionCode: '',
  title: '',
  hint: '',
  weightLabel: '',
  optionsJson: '[]',
  optionsText: '',
  status: 'enabled',
  sortOrder: 0
})

export default {
  data() {
    return {
      domainOptions,
      loading: false,
      list: [],
      total: 0,
      query: emptyQuery(),
      dialogVisible: false,
      dialogTitle: 'New Question',
      form: emptyForm()
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const res = await pbsqQuestionApi.list(this.query)
        const data = res.data || {}
        this.list = data.records || []
        this.total = Number(data.total || 0)
      } finally {
        this.loading = false
      }
    },
    search() {
      this.query.pageNum = 1
      this.loadData()
    },
    resetSearch() {
      this.query = emptyQuery()
      this.loadData()
    },
    handleSizeChange(size) {
      this.query.pageSize = size
      this.query.pageNum = 1
      this.loadData()
    },
    handleCurrentChange(page) {
      this.query.pageNum = page
      this.loadData()
    },
    openCreate() {
      this.dialogTitle = 'New Question'
      this.form = emptyForm()
      this.dialogVisible = true
    },
    openEdit(row) {
      this.dialogTitle = 'Edit Question'
      this.form = {
        ...emptyForm(),
        ...row,
        optionsText: this.optionsToText(row.optionsJson)
      }
      this.dialogVisible = true
    },
    async submit() {
      if (!this.form.questionCode || !this.form.title) {
        this.$message.warning('Please enter the question code and title')
        return
      }
      const payload = this.cleanPayload(this.form)
      if (payload.id) await pbsqQuestionApi.update(payload)
      else await pbsqQuestionApi.create(payload)
      this.$message.success('Saved successfully')
      this.dialogVisible = false
      this.loadData()
    },
    cleanPayload(form) {
      const options = String(form.optionsText || '')
        .split(/\r?\n/)
        .map(item => item.trim())
        .filter(Boolean)
      const { optionsText, ...payload } = form
      return {
        ...payload,
        flowType: 'full',
        domainKey: String(payload.domainKey || '').toLowerCase(),
        questionCode: String(payload.questionCode || '').trim(),
        optionsJson: JSON.stringify(options)
      }
    },
    async remove(row) {
      await this.$confirm(`Delete question ${row.questionCode}?`, 'Confirm', { type: 'warning' })
      await pbsqQuestionApi.remove(row.id)
      this.$message.success('Deleted successfully')
      this.loadData()
    },
    domainLabel(value) {
      const item = domainOptions.find(option => option.value === value)
      return item ? item.label : value
    },
    optionsToText(optionsJson) {
      try {
        const list = JSON.parse(optionsJson || '[]')
        return Array.isArray(list) ? list.join('\n') : ''
      } catch (e) {
        return optionsJson || ''
      }
    },
    optionsSummary(optionsJson) {
      return this.optionsToText(optionsJson).split(/\r?\n/).filter(Boolean).join(' | ')
    }
  }
}
</script>

<style scoped>
.page-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.page-head h3 { margin: 0; }
.search-form { margin-bottom: 12px; }
.pager { margin-top: 16px; text-align: right; }
.danger { color: #f56c6c; }
</style>
